import { HIGHLIGHT_END, HIGHLIGHT_START } from 'types/globalSearch';
import { foldForSearch } from './foldForSearch';
import { tokenize } from './searchQueryParser';

const WORD_SEPARATOR = /[^\p{L}\p{M}\p{N}?]+/u;
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

/**
 * The folded words of a search term that can appear in a result, each as a pattern where `?` stands for
 * one letter, as the backend expands it. `AND`/`OR`/`NOT`, quotes, a leading `-` and `*` are the query's
 * grammar, not its text, and a word after an odd number of `NOT`s is one the results were found without.
 * A quoted word or one with a `?` matches a whole word only: the backend matches those as exact lexemes,
 * every other word as a prefix.
 */
const searchTermPatterns = (searchTerm: string): RegExp[] => {
    const words: { word: string; exact: boolean }[] = [];
    let negated = false;
    for (const token of tokenize(searchTerm)) {
        if (token.type === 'NOT') {
            negated = !negated;
        } else if (token.type === 'TERM' || token.type === 'PHRASE') {
            if (!negated) {
                for (const word of token.value.split(WORD_SEPARATOR)) {
                    words.push({ word: foldForSearch(word), exact: token.type === 'PHRASE' || word.includes('?') });
                }
            }
            negated = false;
        }
    }
    return words
        .filter(({ word }) => word.replaceAll('?', '') !== '')
        .map(
            ({ word, exact }) =>
                new RegExp(word.replaceAll('?', '[\\p{L}\\p{N}]') + (exact ? '(?![\\p{L}\\p{N}])' : ''), 'gu')
        );
};

/**
 * `text` with each word of `searchTerm` marked wherever it starts a word — the backend's match,
 * case- and accent-blind — in the `[[hl]]` sentinels `HighlightedText` renders. The match runs on the
 * folded text but the marks land on the original characters, so the accents stay.
 *
 * Any sentinel `text` already holds is defused first, as the backend does, so a record cannot mark
 * itself.
 */
export const highlightSearchTerm = (text: string, searchTerm: string): string => {
    const escaped = text.replaceAll(HIGHLIGHT_START, '[hl]').replaceAll(HIGHLIGHT_END, '[/hl]');
    const patterns = searchTermPatterns(searchTerm);
    if (patterns.length === 0) {
        return escaped;
    }

    // Folding can change a character's length (ß → ss), so each folded unit remembers its source.
    let folded = '';
    const sourceStart: number[] = [];
    const sourceEnd: number[] = [];
    let offset = 0;
    for (const character of escaped) {
        const foldedCharacter = foldForSearch(character);
        if (foldedCharacter === '' && sourceEnd.length > 0) {
            // A combining mark folds away; it belongs to the letter before it, mark and all.
            sourceEnd[sourceEnd.length - 1] = offset + character.length;
        }
        folded += foldedCharacter;
        for (let unit = 0; unit < foldedCharacter.length; unit++) {
            sourceStart.push(offset);
            sourceEnd.push(offset + character.length);
        }
        offset += character.length;
    }

    const marked = Array.from({ length: escaped.length }, () => false);
    for (const pattern of patterns) {
        for (const { index: at, 0: match } of folded.matchAll(pattern)) {
            if (at === 0 || !WORD_CHARACTER.test(folded[at - 1])) {
                marked.fill(true, sourceStart[at], sourceEnd[at + match.length - 1]);
            }
        }
    }

    let result = '';
    for (let index = 0; index < escaped.length; index++) {
        if (marked[index] !== (index > 0 && marked[index - 1])) {
            result += marked[index] ? HIGHLIGHT_START : HIGHLIGHT_END;
        }
        result += escaped[index];
    }
    return marked[escaped.length - 1] ? result + HIGHLIGHT_END : result;
};
